<template>
  <div class="background-marker primary-color">
    <div style="display:flex;width:100%;justify-content: center;"
      :class="{ 'mobile-view': $vuetify.breakpoint.smAndDown }">
      <div v-if="$vuetify.breakpoint.smAndDown" style="text-align:center;padding: 20px;background-color: white;">
        <img :src="logo" alt="Organization Logo" @error="throwUnknownDomain()" class="login-logo" />
      </div>
      <v-expand-transition>
        <div v-if="!$vuetify.breakpoint.smAndDown || mobileLogin" class="login-page"
          :class="{ 'mobile-page': $vuetify.breakpoint.smAndDown, 'elevation-5': $vuetify.breakpoint.mdAndUp }">
          <v-container fluid :fill-height="$vuetify.breakpoint.mdAndUp" style="padding:20px;">
            <v-form ref="loginForm" @submit="submit" style="width: 100%;">
              <h1 v-if="!$vuetify.breakpoint.smAndDown" style="text-align:center;padding-bottom:26px;z-index:999;">
                <img :src="logo" alt="Organization Logo" @error="throwUnknownDomain()"
                  class="login-logo" />
              </h1>
              <div style="min-height: 400px;">
                <div v-if="postMail" style="text-align:center;padding-top:20px;">
                  <img src="https://media1.tenor.com/images/0b46edf37db3fd5d9ce71c9763bef6af/tenor.gif?itemid=12348454"
                    style="width:150px;text-align:center;" alt="Animation of E-mail Sending">
                  <h2>Enter Verification Code</h2>
                  <div style="margin-bottom: 25px;">
                    We’ve sent you a code to verify your identity. Check your {{ loginType == 0 ? 'email' : 'phone' }} and enter the code we sent you below:
                  </div>
                  
                  <v-alert :value="loginFailed" type="error" outline style="margin-bottom: 20px;">
                    {{ loginFailed }}
                  </v-alert>
                  
                  <div class="otp-container">
                    <input
                      v-for="(num, idx) in 4"
                      :key="'otp-' + idx"
                      :ref="'otp_' + idx"
                      v-model="otpChars[idx]"
                      type="text"
                      maxlength="1"
                      class="otp-field"
                      autocomplete="one-time-code"
                      @input="onOtpInput(idx, $event)"
                      @keydown="onOtpKeydown(idx, $event)"
                      @paste="onOtpPaste($event)"
                      @focus="$event.target.select()"
                    />
                    
                    <span class="otp-divider">-</span>
                    
                    <input
                      v-for="(num, idx) in 4"
                      :key="'otp-' + (idx + 4)"
                      :ref="'otp_' + (idx + 4)"
                      v-model="otpChars[idx + 4]"
                      type="text"
                      maxlength="1"
                      class="otp-field"
                      autocomplete="one-time-code"
                      @input="onOtpInput(idx + 4, $event)"
                      @keydown="onOtpKeydown(idx + 4, $event)"
                      @paste="onOtpPaste($event)"
                      @focus="$event.target.select()"
                    />
                  </div>

                  <v-card-actions>
                    <v-spacer />
                    <v-btn :disabled="loading || verificationCode.length !== 8" color="primary" @click="verifyCode">Verify Code</v-btn>
                    <v-btn @click="postMail = false; verificationCode = ''; loginFailed = ''">Try Again</v-btn>
                    <v-btn flat @click="showHelp = true">Still Can't Log In?</v-btn>
                    <v-spacer />
                  </v-card-actions>
                </div>
                <div v-if="!postMail">
                  <div v-if="$vuetify.breakpoint.smAndDown && newsArticles.length > 0">
                    <v-btn flat small @click="mobileLogin = false">
                      < Back to News </v-btn>
                  </div>
                  <h2 style="font-size: 36px;padding-bottom:10px;text-align:left;">Log In</h2>
                  
                  <div v-if="sessionExists">
                    <div style="font-size: 18px; margin-bottom: 25px; text-align: left;">
                      Your previous session is locked and requires biometric authentication to continue.
                    </div>
                    <v-alert :value="loginFailed" :type="loginAlertType" outline>
                      {{ loginFailed }}
                    </v-alert>
                    <v-alert :value="this.$route.query.error" type="error" outline>
                      Something went wrong. You have been logged out.
                    </v-alert>

                    <div style="margin-top: 30px; display: flex; flex-direction: column; align-items: center;">
                      <v-btn color="primary" large @click="retrySecureLogin" :disabled="loading" style="max-width: 320px;">
                        <v-icon left>mdi-fingerprint</v-icon>
                        Retry Biometric Login
                      </v-btn>

                      <div style="margin-top: 20px;">
                        <a href="javascript:void(0)" @click="loginAsAnotherUser" style="color: #5C68E2; text-decoration: underline; font-size: 16px; font-weight: 500;">
                          Log in as another user
                        </a>
                      </div>
                    </div>

                    <v-card-actions style="margin-top: 30px; padding: 0;">
                      <v-spacer />
                      <v-btn flat @click="showHelp = true; helpStep = 3">Need Help?</v-btn>
                      <v-spacer />
                    </v-card-actions>
                  </div>

                  <div v-else>
                    <div>
                      <span style="font-size: 18px;">
                        Enter your email<span v-if="getDomainInfo && getDomainInfo.text_enabled"> or phone number</span>
                        and we'll send you a verification code to log in if you have an account.
                      </span>
                    </div>
                    <v-alert :value="loginFailed" type="error" outline>
                      {{ loginFailed }}
                    </v-alert>
                    <v-alert :value="this.$route.query.error" type="error" outline>
                      Something went wrong. You have been logged out.
                    </v-alert>
                  </div>
                </div>
                <v-tabs v-if="!postMail && !sessionExists" v-model="loginType" fixed-tabs color="transparent" icons-and-text>
                  <v-tab key="email">
                    Email
                    <v-icon>mdi-email-fast</v-icon>
                  </v-tab>
                  <v-tab key="sms" v-if="getDomainInfo && getDomainInfo.text_enabled">
                    SMS/Text
                    <v-icon>mdi-message-text-fast</v-icon>
                  </v-tab>
                  <v-tab-item key="email" class="inner-tab">
                    <v-alert v-if="checkEmailOutage()" :value="true" type="error">
                      Our email provider is currently experiencing an outage. You may experience delays receiving your
                      verification code.
                      <div v-if="getDomainInfo && getDomainInfo.text_enabled">
                        <br />You may also try logging in via SMS/Text if you have a phone number associated with your
                        account.
                      </div>
                    </v-alert>
                    <v-text-field v-if="loginType == 0" v-model="email"
                      :rules="[v => !!v || 'E-mail is required', v => /.+@.+/.test(v) || 'E-mail must be valid']"
                      autofocus name="login" label="E-mail Address" type="email" outline />
                  </v-tab-item>
                  <v-tab-item key="sms" class="inner-tab">
                    <v-alert :value="true" type="info">
                      Only US numbers are supported at this time.
                    </v-alert>
                    <v-alert v-if="checkSMSOutage()" :value="true" type="error">
                      Our texting provider is currently experiencing an outage. You may experience delays receiving your
                      verification code.<br /><br />
                      Please try logging in via E-mail instead.
                    </v-alert>
                    <v-text-field v-if="loginType == 1" v-model="phone"
                      :rules="[v => !!v || 'Phone number is required', v => /^[0-9]{10}$/.test(v) || 'Phone number must be 10 digits']"
                      autofocus name="login" label="Phone Number" type="tel" outline mask="(###) ###-####" />
                  </v-tab-item>
                </v-tabs>
                <v-card-actions v-if="!postMail && !sessionExists">
                  <v-spacer />
                  <v-btn :disabled="loading" type="submit" color="primary" @click="login">
                    Send Verification Code
                  </v-btn>
                  <v-btn flat @click="showHelp = true; helpStep = 3">Need Help?</v-btn>
                  <v-spacer />
                </v-card-actions>
              </div>
            </v-form>
          </v-container>
        </div>
      </v-expand-transition>
      <v-expand-transition>
        <div
          v-if="(($vuetify.breakpoint.smAndDown && !mobileLogin) || ($vuetify.breakpoint.mdAndUp)) && newsArticles.length > 0"
          class="latest-news elevation-10" :class="{ 'mobile-page': $vuetify.breakpoint.smAndDown }">
          <h1 style="text-align: center;padding-top: 20px;">Latest News</h1>
          <v-container>
            <v-layout v-if="newsArticles.length > 0" v-for="article in newsArticles" align-center justify-center
              fill-height row :key="article.id" style="margin-bottom: 20px;">
              <v-flex xs12>
                <NewsCard :article="article" />
              </v-flex>
            </v-layout>
          </v-container>
        </div>
      </v-expand-transition>
      <v-expand-transition>
        <div v-if="$vuetify.breakpoint.smAndDown && !mobileLogin && newsArticles.length > 0"
          style="text-align: center;background: white;padding: 10px;padding-bottom: 15px;z-index:10;order: 3;padding-bottom: var(--mobile-padding-bottom, initial);"
          class="elevation-10">
          <v-btn style="width: 95%;" color="primary" large @click="mobileLogin = !mobileLogin">
            Log In
          </v-btn>
        </div>
      </v-expand-transition>
    </div>
    <v-dialog v-model="showHelp" width="1200">
      <v-stepper v-model="helpStep">
        <v-expand-transition>
          <v-stepper-header v-if="helpStep > 1">
            <v-stepper-step :complete="helpStep > 1" step="1">Verify your E-mail</v-stepper-step>

            <v-divider></v-divider>

            <v-stepper-step :complete="helpStep > 2" step="2">Check Spam/Junk</v-stepper-step>

            <v-divider></v-divider>

            <v-stepper-step step="3">Contact Us<br /><span style="font-size: 10px;"
                class="legalFoot"></span></v-stepper-step>
          </v-stepper-header>
        </v-expand-transition>

        <v-stepper-items>
          <v-stepper-content step="1" style="text-align:center;">
            <v-card-text>
              <h2 style="font-weight: 300;">We're sorry you're having trouble logging in.</h2>
              <h3 style="font-weight: 300;">Just to double check, please verify your e-mail is correct:</h3>
              <br />
              <div
                style="margin-top: 15px;display: inline;background: #E0E0E0;border: 1px solid black;padding: 10px;font-family: monospace;font-size: 18px;">
                {{ email }}
              </div>
              <br />
              <br />
              <div>
                <span>
                  It's common to misspell your e-mail address or the provider.<br />Check after the @ to ensure it's
                  going to the right place.
                  <br />
                  <br />
                  If it's correct, click Next below to continue troubleshooting or click Back to Login to go back.
                </span>
              </div>
            </v-card-text>
          </v-stepper-content>

          <v-stepper-content step="2" style="text-align:center;">
            <v-card-text>
              <h2 style="font-weight: 300;">Please check your Spam or Junk folder</h2>
              <h3 style="font-weight: 300;">Even legitimate e-mails can be mistakenly sent there.</h3>
              <br />
              <img src="/assets/gmail_check.gif" style="max-width: 400px;" />
              <br />
              <br />
              <div>
                <span>
                  Your spam folder might be hidden by your provider.<br />
                  If you can't find a Spam/Junk folder, look for a "More" button
                  <br />
                  If there's no e-mail from us in your Spam/Junk folder, click "Next" below.
                </span>
              </div>
            </v-card-text>
          </v-stepper-content>

          <v-stepper-content step="3" style="text-align:center;">
            <v-card-text>
              <h2 style="font-weight: 300;">Having issues logging in?</h2>
              <h3 style="font-weight: 300;">There may be a temporary issue or an issue between us and your
                provider,<br />or we may have the wrong e-mail on file.</h3>
              <br />
              <h3 style="font-weight: 300;">Please contact <span class="legalFoot"></span><br /> for assistance.</h3>

              <div v-if="support_info && (support_info.phone || support_info.email)"
                style="background: #FFFF88;margin-top: 10px;padding: 10px;border: 3px solid black;font-size: 16px;display: inline-block;text-align:center;font-family: monospace;">
                <span v-if="support_info.phone">
                  <b>By Phone</b>: <span>{{ support_info.phone }}</span><br />
                </span>
                <span v-if="support_info.email">
                  <b>By E-mail</b>: <span>{{ support_info.email }}</span>
                </span>
              </div>

              <div v-if="getAppVersion"
                style="margin-top: 10px;font-size: 16px;font-family: monospace;background: #FFFF88;padding: 10px;border: 3px solid black;display: inline-block;text-align:center;">
                App v{{ getAppVersion }}
              </div>

              <br />
            </v-card-text>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
      <v-card-actions style="background: #E0E0E0;">

        <v-btn @click="showHelp = false">
          Back to Login
        </v-btn>

        <v-spacer />

        <v-btn @click="(helpStep == 1 ? showHelp = false : helpStep--)">
          Previous
        </v-btn>

        <v-btn v-if="helpStep !== 3" color="primary" @click="helpStep++">
          Next
        </v-btn>
      </v-card-actions>
    </v-dialog>
    <Preloader v-if="loading || isCheckingSession" />
  </div>
</template>

<script>
import Preloader from '@/components/global/Preloader'
import { mapGetters } from 'vuex'
import api from '@/api'
import NewsCard from '../../NewsCard.vue';
import moment from 'moment-timezone'
import axios from 'axios'

export default {
  name: 'Login',
  props: ['logo'],
  components: {
    Preloader,
    NewsCard
  },
  data() {
    return {
      email: '',
      phone: '',
      loading: false,
      loginType: 0,
      loginFailed: '',
      postMail: false,
      error: '',
      showHelp: false,
      helpStep: 1,
      support_info: null,
      newsArticles: [],
      mobileLogin: false,
      sessionExists: false,
      verificationCode: '',
      loginAlertType: 'error',
      otpChars: ['', '', '', '', '', '', '', '']
    }
  },
  computed: {
    ...mapGetters('auth', [
      'loggedIn',
      'getDomainInfo',
      'isCheckingSession',
      'getAppVersion'
    ]),
  },
  watch: {
    loggedIn() {
      if (this.loggedIn) {
        this.$router.replace(this.$route.query.from || { name: 'Home' })
      }
    },
    helpStep() {
      if (this.helpStep == 3) {
        this.getSupport()
      }
    },
    postMail(val) {
      if (val) {
        this.otpChars = ['', '', '', '', '', '', '', ''];
        this.$nextTick(() => {
          if (this.$refs['otp_0'] && this.$refs['otp_0'][0]) {
            this.$refs['otp_0'][0].focus();
          }
        });
      }
    }
  },
  methods: {
    retrySecureLogin() {
      if (this.isMobileApp()) {
        this.postMobileMessage('retry-auth')
      }
    },
    loginAsAnotherUser() {
      if (this.isMobileApp()) {
        this.postMobileMessage('logOut')
      }
      this.sessionExists = false
      if (this.$route.query.sessionExists !== undefined) {
        const query = { ...this.$route.query }
        delete query.sessionExists
        this.$router.replace({ query }).catch(() => {})
      }
    },
    checkEmailOutage() {
      if ('outage' in this.getDomainInfo && this.getDomainInfo.outage.includes("email")) {
        return true
      } else {
        return false
      }
    },
    checkSMSOutage() {
      if ('outage' in this.getDomainInfo && this.getDomainInfo.outage.includes("sms")) {
        return true
      } else {
        return false
      }
    },
    login: function () {
      if (this.$refs.loginForm.validate()) {
        this.loading = true
        var query_data = {}
        if (this.loginType == 0)
          query_data = { email: this.email, from: this.$route.query.from || null , timezone: moment.tz.guess()}
        if (this.loginType == 1)
          query_data = { phone: this.phone, from: this.$route.query.from || null }
        this.$store.dispatch('auth/clientLogIn', query_data)
          .then((data) => {
            if ('auth' in data) {
              this.verificationCode = data['auth']
              this.verifyCode()
            } else {
              this.loading = false
              this.loginFailed = ''
              this.postMail = true
            }
          }, (error) => {
            this.loading = false
            this.loginFailed = (error.response && error.response.data && error.response.data.error) || 'Failed to send verification code. Check your input then try again.'
            this.loginAlertType = "error"
          })
      }
    },
    verifyCode: function () {
      if (this.verificationCode) {
        this.loading = true
        const payload = {
          code: this.verificationCode,
          email: this.loginType === 0 ? this.email : null,
          phone: this.loginType === 1 ? this.phone : null
        }
        api.auth.auth.verifyCode(payload)
          .then((data) => {
            this.$store.dispatch('auth/setCookieSession', {
              user: data.email,
              name: `${data.first_name} ${data.last_name}`,
              id: data.id
            }).then(() => {
              this.loading = false
              
              if (this.getAppVersion) {
                api.auth.auth.getAppToken()
                  .then((tokenResponse) => {
                    const longLivedToken = tokenResponse.token
                    if (this.isMobileApp()) {
                      this.postMobileMessage('app-token:' + longLivedToken)
                    }
                  })
              }
              
              this.$router.replace(this.$route.query.from || { name: 'Home' })
            })
          })
          .catch((error) => {
            this.loading = false;
            this.loginFailed = (error.response && error.response.data && error.response.data.error) || 'Invalid or expired verification code.';
            this.loginAlertType = "error"
            this.otpChars = ['', '', '', '', '', '', '', ''];
            this.verificationCode = '';
            this.$nextTick(() => {
              if (this.$refs['otp_0'] && this.$refs['otp_0'][0]) {
                this.$refs['otp_0'][0].focus();
              }
            });
          })
      }
    },
    onOtpInput(index, event) {
      const val = event.target.value.toUpperCase();
      this.$set(this.otpChars, index, val.slice(-1));
      if (val && index < 7) {
        this.$nextTick(() => {
          if (this.$refs['otp_' + (index + 1)] && this.$refs['otp_' + (index + 1)][0]) {
            this.$refs['otp_' + (index + 1)][0].focus();
          }
        });
      }
      this.checkOtpAndSubmit();
    },
    onOtpKeydown(index, event) {
      if (event.key === 'Backspace') {
        if (!this.otpChars[index] && index > 0) {
          this.$set(this.otpChars, index - 1, '');
          this.$nextTick(() => {
            if (this.$refs['otp_' + (index - 1)] && this.$refs['otp_' + (index - 1)][0]) {
              this.$refs['otp_' + (index - 1)][0].focus();
            }
          });
        } else {
          this.$set(this.otpChars, index, '');
        }
        event.preventDefault();
        this.checkOtpAndSubmit();
      }
    },
    onOtpPaste(event) {
      const pasteData = (event.clipboardData || window.clipboardData).getData('text');
      const cleanData = pasteData.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase();
      for (let i = 0; i < cleanData.length; i++) {
        this.$set(this.otpChars, i, cleanData[i]);
      }
      const nextFocus = Math.min(cleanData.length, 7);
      this.$nextTick(() => {
        if (this.$refs['otp_' + nextFocus] && this.$refs['otp_' + nextFocus][0]) {
          this.$refs['otp_' + nextFocus][0].focus();
        }
      });
      event.preventDefault();
      this.checkOtpAndSubmit();
    },
    checkOtpAndSubmit() {
      const fullCode = this.otpChars.join('');
      this.verificationCode = fullCode;
      if (fullCode.length === 8) {
        this.verifyCode();
      }
    },
    submit: function (e) {
      e.preventDefault()
    },
    throwUnknownDomain() {
      this.$router.push({ name: 'ErrorOrgNotFound' })
    },
    getSupport() {
      api.auth.organization.getSupport()
        .then((response) => {
          this.support_info = response
        })
    }
  },
  created: function () {
    this.documentTitle('Log In')
    if (this.loggedIn) {
      this.$router.replace(this.$route.query.from || { name: 'Home' })
    }
    if (this.$route.query.sessionExists !== undefined && this.$route.query.sessionExists !== 'false' && this.$route.query.sessionExists !== '0') {
      this.sessionExists = true
      this.mobileLogin = true
    }
    const token = this.$route.query.token
    if (token && !this.loggedIn) {
      this.loading = true
      this.mobileLogin = true
      this.$store.dispatch('auth/magicLogIn', token)
        .then(() => {
          this.loading = false
          if (this.getAppVersion) {
            api.auth.auth.getAppToken()
              .then((tokenResponse) => {
                const longLivedToken = tokenResponse.token
                console.log('app-token:' + longLivedToken)
                if (this.isMobileApp()) {
                  this.postMobileMessage('app-token:' + longLivedToken)
                }
              })
          }
        }, (error) => {
          this.loading = false
          this.loginFailed = (error.response && error.response.data && error.response.data.error) || 'That link didn\'t work. If you clicked on an old login link, enter your phone number or email to request a new one. Login links can only be used once, and expire after 15 minutes.'
          if (error.response && error.response.data && error.response.data.type) {
            if (error.response.data.type == 'info') {
              this.loginAlertType = "info"
            } else {
              this.loginAlertType = "error"
            }
          }
          if (this.getAppVersion) {
            if (this.isMobileApp()) {
              this.postMobileMessage('login-failed')
            }
            console.log('app-login-failed')
          }
        })
    }
    api.news.news.list()
      .then((data) => {
        this.newsArticles = data.results
      })
      .finally(() => {
        if (this.$vuetify.breakpoint.smAndDown && this.newsArticles.length === 0) {
          this.mobileLogin = true
        }
      })
  },
  beforeRouteUpdate(to, from, next) {
    if (to.name === 'Login' && this.loggedIn) {
      this.$router.replace(to.query.from || { name: 'Home' })
    } else {
      if (to.query.sessionExists !== undefined && to.query.sessionExists !== 'false' && to.query.sessionExists !== '0') {
        this.sessionExists = true
        this.mobileLogin = true
      }
      next()
    }
  }
}
</script>

<style scoped>
.client {
  position: relative;
  overflow: hidden;
}

.mobile-view {
  flex-direction: column;
  padding-top: var(--mobile-padding-top, initial);
}

.mobile-page {
  max-width: unset !important;
  width: 100%;
}

.login-page {
  background: #FDFDFD;
  max-width: 700px;
  width: 100%;
  height: 100%;
  z-index: 10;
}

.latest-news.mobile-page {
  order: 1;
}

.login-page.mobile-page {
  order: 2;
  height: unset;
  flex-grow: 1;
}

.otp-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 440px;
  margin: 0 auto 30px auto;
  padding: 0 4px;
  box-sizing: border-box;
  gap: 6px;
}

.otp-field {
  width: 100%;
  max-width: 40px;
  height: 50px;
  min-width: 0;
  flex: 1 1 0;
  font-size: 22px;
  text-align: center;
  border: 2px solid #5C68E2;
  border-radius: 6px;
  background: white;
  text-transform: uppercase;
  font-family: monospace;
  font-weight: bold;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  padding: 0;
}

.otp-field:focus {
  border-color: #3b48b5;
  box-shadow: 0 0 0 2px rgba(92, 104, 226, 0.25);
}

.otp-divider {
  font-size: 24px;
  font-weight: bold;
  color: #5C68E2;
  margin: 0 2px;
  flex-shrink: 0;
  user-select: none;
}

@media (max-width: 420px) {
  .otp-container {
    gap: 3px;
    padding: 0 2px;
  }
  .otp-field {
    height: 44px;
    font-size: 18px;
    border-width: 1.5px;
    border-radius: 4px;
  }
  .otp-divider {
    font-size: 18px;
    margin: 0 1px;
  }
}

@media (max-width: 340px) {
  .otp-container {
    gap: 2px;
    padding: 0;
  }
  .otp-field {
    height: 38px;
    font-size: 15px;
    border-width: 1px;
  }
  .otp-divider {
    font-size: 15px;
    margin: 0;
  }
}

.background-marker {
  height: 100%;
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1575' height='1312.5' viewBox='0 0 1080 900'%3E%3Cg fill-opacity='0.11'%3E%3Cpolygon fill='%23444' points='90 150 0 300 180 300'/%3E%3Cpolygon points='90 150 180 0 0 0'/%3E%3Cpolygon fill='%23AAA' points='270 150 360 0 180 0'/%3E%3Cpolygon fill='%23DDD' points='450 150 360 300 540 300'/%3E%3Cpolygon fill='%23999' points='450 150 540 0 360 0'/%3E%3Cpolygon points='630 150 540 300 720 300'/%3E%3Cpolygon fill='%23DDD' points='630 150 720 0 540 0'/%3E%3Cpolygon fill='%23444' points='810 150 720 300 900 300'/%3E%3Cpolygon fill='%23FFF' points='810 150 900 0 720 0'/%3E%3Cpolygon fill='%23DDD' points='990 150 900 300 1080 300'/%3E%3Cpolygon fill='%23444' points='990 150 1080 0 900 0'/%3E%3Cpolygon fill='%23DDD' points='90 450 0 600 180 600'/%3E%3Cpolygon points='90 450 180 300 0 300'/%3E%3Cpolygon fill='%23666' points='270 450 180 600 360 600'/%3E%3Cpolygon fill='%23AAA' points='270 450 360 300 180 300'/%3E%3Cpolygon fill='%23DDD' points='450 450 360 600 540 600'/%3E%3Cpolygon fill='%23999' points='450 450 540 300 360 300'/%3E%3Cpolygon fill='%23999' points='630 450 540 600 720 600'/%3E%3Cpolygon fill='%23FFF' points='630 450 720 300 540 300'/%3E%3Cpolygon points='810 450 720 600 900 600'/%3E%3Cpolygon fill='%23DDD' points='810 450 900 300 720 300'/%3E%3Cpolygon fill='%23AAA' points='990 450 900 600 1080 600'/%3E%3Cpolygon fill='%23444' points='990 450 1080 300 900 300'/%3E%3Cpolygon fill='%23222' points='90 750 0 900 180 900'/%3E%3Cpolygon points='270 750 180 900 360 900'/%3E%3Cpolygon fill='%23DDD' points='270 750 360 600 180 600'/%3E%3Cpolygon points='450 750 540 600 360 600'/%3E%3Cpolygon points='630 750 540 900 720 900'/%3E%3Cpolygon fill='%23444' points='630 750 720 600 540 600'/%3E%3Cpolygon fill='%23AAA' points='810 750 720 900 900 900'/%3E%3Cpolygon fill='%23666' points='810 750 900 600 720 600'/%3E%3Cpolygon fill='%23999' points='990 750 900 900 1080 900'/%3E%3Cpolygon fill='%23999' points='180 0 90 150 270 150'/%3E%3Cpolygon fill='%23444' points='360 0 270 150 450 150'/%3E%3Cpolygon fill='%23FFF' points='540 0 450 150 630 150'/%3E%3Cpolygon points='900 0 810 150 990 150'/%3E%3Cpolygon fill='%23222' points='0 300 -90 450 90 450'/%3E%3Cpolygon fill='%23FFF' points='0 300 90 150 -90 150'/%3E%3Cpolygon fill='%23FFF' points='180 300 90 450 270 450'/%3E%3Cpolygon fill='%23666' points='180 300 270 150 90 150'/%3E%3Cpolygon fill='%23222' points='360 300 270 450 450 450'/%3E%3Cpolygon fill='%23FFF' points='360 300 450 150 270 150'/%3E%3Cpolygon fill='%23444' points='540 300 450 450 630 450'/%3E%3Cpolygon fill='%23222' points='540 300 630 150 450 150'/%3E%3Cpolygon fill='%23AAA' points='720 300 630 450 810 450'/%3E%3Cpolygon fill='%23666' points='720 300 810 150 630 150'/%3E%3Cpolygon fill='%23FFF' points='900 300 810 450 990 450'/%3E%3Cpolygon fill='%23999' points='900 300 990 150 810 150'/%3E%3Cpolygon points='0 600 -90 750 90 750'/%3E%3Cpolygon fill='%23666' points='0 600 90 450 -90 450'/%3E%3Cpolygon fill='%23AAA' points='180 600 90 750 270 750'/%3E%3Cpolygon fill='%23444' points='180 600 270 450 90 450'/%3E%3Cpolygon fill='%23444' points='360 600 270 750 450 750'/%3E%3Cpolygon fill='%23999' points='360 600 450 450 270 450'/%3E%3Cpolygon fill='%23666' points='540 600 630 450 450 450'/%3E%3Cpolygon fill='%23222' points='720 600 630 750 810 750'/%3E%3Cpolygon fill='%23FFF' points='900 600 810 750 990 750'/%3E%3Cpolygon fill='%23222' points='900 600 990 450 810 450'/%3E%3Cpolygon fill='%23DDD' points='0 900 90 750 -90 750'/%3E%3Cpolygon fill='%23444' points='180 900 270 750 90 750'/%3E%3Cpolygon fill='%23FFF' points='360 900 450 750 270 750'/%3E%3Cpolygon fill='%23AAA' points='540 900 630 750 450 750'/%3E%3Cpolygon fill='%23FFF' points='720 900 810 750 630 750'/%3E%3Cpolygon fill='%23222' points='900 900 990 750 810 750'/%3E%3Cpolygon fill='%23222' points='1080 300 990 450 1170 450'/%3E%3Cpolygon fill='%23FFF' points='1080 300 1170 150 990 150'/%3E%3Cpolygon points='1080 600 990 750 1170 750'/%3E%3Cpolygon fill='%23666' points='1080 600 1170 450 990 450'/%3E%3Cpolygon fill='%23DDD' points='1080 900 1170 750 990 750'/%3E%3C/g%3E%3C/svg%3E");
  background-attachment: fixed;
  background-repeat: repeat;
  display: flex;
}

.inner-tab {
  background: rgba(26, 26, 26, 0.02);
  padding: 10px;
  border-radius: 10px;
}

.latest-news {
  background-color: #E0E0E0;
  overflow-y: auto;
  max-width: 500px;
  width: 100%;
  height: 100%;
  z-index: 9;
}

::-webkit-scrollbar {
  width: 5px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #888;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
